import React, { useState } from "react";
import { Alert } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { KnowledgeItem } from "@jugl-web/rest-api/ai-workflows/types";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { useKnowledgeTraining } from "@web-src/modules/cx/providers/KnowledgeTrainingProvider";
import { AgentKnowledgeContentContainer } from "../AgentKnowledgeContentContainer";
import { ReactComponent as TextIcon } from "./assets/text.svg";
import { AgentKnowledgeItemContainer } from "../AgentKnowledgeItemContainer";
import { useAgentKnowledge } from "../../../../../../hooks/useAgentKnowledge";
import { TextKnowledgeForm } from "./components/TextKnowledgeForm";

export const AgentKnowledgeTextContent: React.FC<{
  entityId: string;
}> = ({ entityId }) => {
  const { t } = useTranslations();
  const { trainingStatus } = useKnowledgeTraining();
  const { textKnowledge, handleDeleteKnowledge, isAnyKnowledgeFetching } =
    useAgentKnowledge(entityId);
  const [isFormOpen, setIsFormOpen] = useState(!textKnowledge?.length);
  const [editedKnowledge, setEditedKnowledge] =
    useState<KnowledgeItem<"text">>();
  const { isSubscriptionCanceled } = useSubscriptionV2();

  const deleteConfirmationAlertState = useConfirmationDialogState();

  const hasItems = !!textKnowledge?.length;

  if (isFormOpen || !hasItems || editedKnowledge) {
    return (
      <TextKnowledgeForm
        entityId={entityId}
        isFirstItem={!hasItems}
        isDisabled={isAnyKnowledgeFetching || isSubscriptionCanceled}
        onRequestClose={() => {
          setIsFormOpen(false);
          setEditedKnowledge(undefined);
        }}
        editedKnowledge={editedKnowledge}
      />
    );
  }

  return (
    <>
      <AgentKnowledgeContentContainer
        icon={<TextIcon />}
        title={t({
          id: "cx-ai-agents-page.text",
          defaultMessage: "Text",
        })}
        description={t({
          id: "cx-ai-agents-page.add-text-to-train-your-ai-agent",
          defaultMessage:
            "Add plain text sources or instructions to train your AI Agent",
        })}
        onAddItem={
          !isSubscriptionCanceled && trainingStatus !== "in_process"
            ? () => setIsFormOpen(true)
            : undefined
        }
      >
        <div className="flex flex-col gap-4">
          <span className="font-secondary font-medium text-black">
            {t({
              id: "cx-ai-agents-page.added-snippets",
              defaultMessage: "Added Snippets",
            })}
          </span>
          <div className="flex flex-col gap-6">
            {textKnowledge.map((item) => (
              <AgentKnowledgeItemContainer
                key={item.data.id}
                title={item.data.metadata?.title || ""}
                icon={<TextIcon className="w-5" />}
                onDelete={
                  !isSubscriptionCanceled
                    ? () =>
                        deleteConfirmationAlertState.open({
                          onConfirm: () =>
                            handleDeleteKnowledge(item.data.id, "text"),
                        })
                    : undefined
                }
                onClick={() => setEditedKnowledge(item)}
              >
                {item.data.source}
              </AgentKnowledgeItemContainer>
            ))}
          </div>
        </div>
      </AgentKnowledgeContentContainer>
      <Alert
        isOpen={deleteConfirmationAlertState.isOpen}
        onRequestClose={deleteConfirmationAlertState.close}
        title={t({
          id: "cx-ai-agents-page.delete-item",
          defaultMessage: "Delete Item",
        })}
        content={t({
          id: "cx-ai-agents-page.delete-text-knowledge-description-no-longer-trained",
          defaultMessage:
            "If you will delete selected Text Snippet, AI Agent will no longer be trained on its info",
        })}
        buttons={[
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            role: "close",
          },
          {
            text: t({ id: "common.delete", defaultMessage: "Delete" }),
            color: "tertiary",
            onClick: () => {
              deleteConfirmationAlertState.confirm({
                closeOnceConfirmed: true,
              });
            },
          },
        ]}
      />
    </>
  );
};
